import { EvaluationRecipientStatus, EvaluationRequestStatus } from './evaluationContract';

export type StatusMeta = {
  labelKey: string;
  fallback: string;
  color: string;
  backgroundColor: string;
};

export type Swatch = Pick<StatusMeta, 'color' | 'backgroundColor'>;

const BLUE: Swatch = { color: 'var(--blue-800)', backgroundColor: 'var(--blue-100)' };
const RED: Swatch = { color: 'var(--red-700)', backgroundColor: 'var(--red-100)' };
const GREEN: Swatch = { color: 'var(--green-700)', backgroundColor: 'var(--green-100)' };
const YELLOW: Swatch = { color: 'var(--yellow-800)', backgroundColor: 'var(--yellow-100)' };
const GREY: Swatch = { color: 'var(--gray-700)', backgroundColor: 'var(--gray-200)' };

const UNKNOWN: StatusMeta = { labelKey: 'status.unknown', fallback: 'Okänd', ...GREY };

const REQUEST_STATUS: Record<string, [string, Swatch]> = {
  [EvaluationRequestStatus.Active]: ['Aktiv', BLUE],
  [EvaluationRequestStatus.Overdue]: ['Försenad', RED],
  [EvaluationRequestStatus.Completed]: ['Klar', GREEN],
  [EvaluationRequestStatus.Closed]: ['Stängd', GREY],
  [EvaluationRequestStatus.Cancelled]: ['Avbruten', GREY],
};

const RECIPIENT_STATUS: Record<string, [string, Swatch]> = {
  [EvaluationRecipientStatus.Answered]: ['Besvarad', GREEN],
  [EvaluationRecipientStatus.Sent]: ['Väntar', YELLOW],
  [EvaluationRecipientStatus.Opened]: ['Väntar · öppnad', YELLOW],
  [EvaluationRecipientStatus.Overdue]: ['Försenad', RED],
  [EvaluationRecipientStatus.Failed]: ['Misslyckades', RED],
  [EvaluationRecipientStatus.NotSent]: ['Ej skickad', GREY],
  [EvaluationRecipientStatus.Declined]: ['Kan inte bedöma', GREY],
  [EvaluationRecipientStatus.Closed]: ['Stängd', GREY],
  [EvaluationRecipientStatus.Revoked]: ['Återkallad', GREY],
};

const metaFrom = (table: Record<string, [string, Swatch]>, prefix: string, status: string) => {
  const entry = Object.prototype.hasOwnProperty.call(table, status) ? table[status] : undefined;
  if (!entry) return UNKNOWN;
  const [fallback, swatch] = entry;
  return { labelKey: `${prefix}.${status}`, fallback, ...swatch };
};

export const requestStatusMeta = (status: string): StatusMeta =>
  metaFrom(REQUEST_STATUS, 'status.request', status);

export const recipientStatusMeta = (status: string): StatusMeta =>
  metaFrom(RECIPIENT_STATUS, 'status.recipient', status);

export const TAG_CHIP_STYLE: Swatch = GREY;

export const TAG_ARCHIVED_LABEL = { labelKey: 'tag.archived', fallback: 'arkiverad' };

export const SCORE_BAR = {
  fill: 'var(--gray-700)',
  fewAnswersFill: 'var(--gray-300)',
  track: 'var(--gray-200)',
  minAnswers: 3,
};

export const PROGRESS_BAR = { fill: 'var(--green-500)', track: 'var(--gray-200)' };

const TREND_UP: Swatch = { color: 'var(--green-700)', backgroundColor: 'var(--green-50)' };
const TREND_DOWN: Swatch = { color: 'var(--red-700)', backgroundColor: 'var(--red-50)' };

export const trendTone = (delta: number): Swatch => (delta >= 0 ? TREND_UP : TREND_DOWN);
